import React, { useState, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  Typography,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  PlayArrow,
  Pause,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { capitalize, isEqual } from 'lodash';

import { MainLayout } from '../components/layout/MainLayout';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { CreateCampaignModal } from '../components/CreateCampaignModal';
import { campaignAPI, llmTemplatesAPI } from '../services/api';
import { useToast } from '../components/common/ToastProvider';
import { Campaign } from '../services/interfaces';
import { VOICE_OPTIONS } from '../consts';

const parseBusinessDays = (value: unknown): Record<string, boolean> => {
  const defaults = {
    mon: true,
    tue: true,
    wed: true,
    thu: true,
    fri: true,
    sat: false,
    sun: false,
  };
  if (!value) return defaults;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as Record<string, boolean>;
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  }
  if (typeof value === 'object' && value !== null) {
    return { ...defaults, ...(value as Record<string, boolean>) };
  }
  return defaults;
};

const VOICE_LABELS: Record<string, string> = VOICE_OPTIONS.reduce(
  (acc, v) => ({ ...acc, [v.value]: v.label }),
  {}
);

// Field labels mapping
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  status: 'Status',
  createdAt: 'Created At',
  startedAt: 'Started At',
  completedAt: 'Completed At',
  errorMessage: 'Error Message',
  llmTemplate: 'Template Name',
  llmTemplateIsDefault: 'Is Template Default',
  user: 'User Name',
  businessDays: 'Business Days',
  businessHours: 'Start Time - End Time',
  timezone: 'Timezone',
  ttsProvider: 'TTS Provider',
  maxCallDurationSec: 'Max Call Duration',
  ttsVoiceModel: 'Voice Model',
  pauseReason: 'Pause Reason',
};

// Field order for view modal
const WEEK_DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const HOURS = [
  '12:00 AM',
  '01:00 AM',
  '02:00 AM',
  '03:00 AM',
  '04:00 AM',
  '05:00 AM',
  '06:00 AM',
  '07:00 AM',
  '08:00 AM',
  '09:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '01:00 PM',
  '02:00 PM',
  '03:00 PM',
  '04:00 PM',
  '05:00 PM',
  '06:00 PM',
  '07:00 PM',
  '08:00 PM',
  '09:00 PM',
  '10:00 PM',
  '11:00 PM',
];

const FIELD_ORDER = [
  'name',
  'description',
  'status',
  'createdAt',
  'startedAt',
  'completedAt',
  'errorMessage',
  'llmTemplate',
  'llmTemplateIsDefault',
  'user',
  'businessDays',
  'businessHours',
  'timezone',
  'ttsProvider',
  'maxCallDurationSec',
  'ttsVoiceModel',
];

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const getChangedFields = (
  original: Campaign | null,
  edited: Campaign | null
): { field: string; oldValue: string; newValue: string }[] => {
  if (!original || !edited) return [];
  const changes: { field: string; oldValue: string; newValue: string }[] = [];
  const fieldsToCompare: (keyof Campaign)[] = [
    'name',
    'description',
    'maxCallDurationSec',
    'ttsVoiceModel',
    'businessDays',
    'startTime',
    'endTime',
  ];

  fieldsToCompare.forEach((field) => {
    const oldVal = original[field];
    const newVal = edited[field];
    const hasChanged =
      typeof oldVal === 'object' && typeof newVal === 'object'
        ? !isEqual(oldVal, newVal)
        : oldVal !== newVal;
    if (hasChanged) {
      const formatVal = (val: unknown) => {
        if (val === undefined || val === null) return '(empty)';
        if (typeof val === 'object' && val !== null) {
          const entries = Object.entries(val).filter(([, v]) => v);
          return entries.map(([k]) => capitalize(k)).join(', ') || '(none)';
        }
        return String(val);
      };
      changes.push({
        field: String(field),
        oldValue: formatVal(oldVal),
        newValue: formatVal(newVal),
      });
    }
  });

  return changes;
};

const STARTABLE_STATUSES = ['pending', 'paused', 'failed'];
const PAUSABLE_STATUSES = ['running', 'scheduled'];
const RESUMABLE_STATUSES = ['paused'];
const EDITABLE_STATUSES = ['pending', 'paused', 'failed'];
const DELETABLE_STATUSES = ['pending', 'paused', 'failed', 'scheduled'];

const canStartCampaign = (status: string): boolean => {
  return STARTABLE_STATUSES.includes(status.toLowerCase());
};

const canPauseCampaign = (status: string): boolean => {
  return PAUSABLE_STATUSES.includes(status.toLowerCase());
};

const canResumeCampaign = (status: string): boolean => {
  return RESUMABLE_STATUSES.includes(status.toLowerCase());
};

const canEditCampaign = (status: string): boolean => {
  return EDITABLE_STATUSES.includes(status.toLowerCase());
};

const canDeleteCampaign = (status: string): boolean => {
  return DELETABLE_STATUSES.includes(status.toLowerCase());
};

const getStartDisabledTooltip = (status: string): string => {
  return `Cannot start campaign while status is ${status}. Allowed statuses: ${STARTABLE_STATUSES.join(
    ', '
  )}`;
};

const getEditDisabledTooltip = (status: string): string => {
  return `Cannot edit campaign while status is ${status}. Allowed statuses: ${EDITABLE_STATUSES.join(
    ', '
  )}`;
};

const getDeleteDisabledTooltip = (status: string): string => {
  return `Cannot delete campaign while status is ${status}. Allowed statuses: ${DELETABLE_STATUSES.join(
    ', '
  )}`;
};

const CAMPAIGN_STATUSES = [
  'completed',
  'failed',
  'paused',
  'pending',
  'running',
  'scheduled',
];

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  running: 'Running',
  paused: 'Paused',
  failed: 'Failed',
  completed: 'Completed',
  scheduled: 'Scheduled',
};

export const CampaignsPage: React.FC = () => {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const [inputFilters, setInputFilters] = useState(() => ({
    name: searchParams.get('name') || '',
    createdAtFrom: searchParams.get('createdAtFrom') || '',
    createdAtTo: searchParams.get('createdAtTo') || '',
    status: searchParams.get('status') || '',
  }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    name: searchParams.get('name') || '',
    createdAtFrom: searchParams.get('createdAtFrom') || '',
    createdAtTo: searchParams.get('createdAtTo') || '',
    status: searchParams.get('status') || '',
  }));

  React.useEffect(() => {
    const filters = {
      name: searchParams.get('name') || '',
      createdAtFrom: searchParams.get('createdAtFrom') || '',
      createdAtTo: searchParams.get('createdAtTo') || '',
      status: searchParams.get('status') || '',
    };
    setInputFilters(filters);
    setAppliedFilters(filters);
    setPage(0);
  }, [searchParams]);

  // Queries
  const getCampaignsQuery = useQuery(
    ['campaignAPI.getAllCampaigns', page, appliedFilters],
    () =>
      campaignAPI.getAllCampaigns({
        page,
        ...(appliedFilters.name ? { name: appliedFilters.name } : {}),
        ...(appliedFilters.createdAtFrom
          ? { createdAtFrom: appliedFilters.createdAtFrom }
          : {}),
        ...(appliedFilters.createdAtTo
          ? { createdAtTo: appliedFilters.createdAtTo }
          : {}),
        ...(appliedFilters.status ? { status: appliedFilters.status } : {}),
      }),
    {
      refetchInterval: 5000,
      onError: (err) => {
        console.error(err);
        showToast('Failed to load campaigns', 'error');
      },
    }
  );

  const getTemplatesQuery = useQuery(
    ['llmTemplatesAPI.getTemplates'],
    () => llmTemplatesAPI.getTemplates(),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to load templates', 'error');
      },
    }
  );

  // Mutations
  const createCampaignMutation = useMutation(
    (data: {
      businessDays: Record<string, boolean>;
      description?: string;
      endTime: string;
      file: File;
      maxCallDurationSec: number;
      name: string;
      startTime: string;
      templateId: string;
      timezone: string;
      ttsVoiceModel: string;
    }) => campaignAPI.createCampaign(data),
    {
      onError: (err: any) => {
        console.error(err);
        showToast(
          err?.message ? err.message : 'Failed to create campaign',
          'error'
        );
      },
      onSuccess: () => {
        showToast('Campaign created successfully', 'success');
        getCampaignsQuery.refetch();
        handleCloseCreateModal();
      },
    }
  );

  const updateCampaignMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Campaign> }) =>
      campaignAPI.updateCampaign(id, data),
    {
      onError: (err: any) => {
        console.error(err);
        showToast(
          err?.message ? err.message : 'Failed to update campaign',
          'error'
        );
      },
      onSuccess: () => {
        showToast('Campaign updated successfully', 'success');
        getCampaignsQuery.refetch();
        setConfirmUpdateOpen(false);
        setEditModalOpen(false);
        setEditCampaign(null);
        setOriginalCampaign(null);
      },
    }
  );

  const deleteCampaignMutation = useMutation(
    (id: string) => campaignAPI.deleteCampaign(id),
    {
      onError: (err: any) => {
        console.error(err);
        showToast(
          err?.message ? err.message : 'Failed to delete campaign',
          'error'
        );
      },
      onSuccess: () => {
        showToast('Campaign deleted successfully', 'success');
        getCampaignsQuery.refetch();
        setConfirmDeleteOpen(false);
        setSelectedCampaignId(null);
      },
    }
  );

  const startCampaignMutation = useMutation(
    (id: string) => campaignAPI.startCampaign(id),
    {
      onError: (err: any) => {
        console.error(err);
        showToast(
          err?.message ? err.message : 'Failed to start campaign',
          'error'
        );
      },
      onSuccess: () => {
        showToast('Campaign started successfully', 'success');
        getCampaignsQuery.refetch();
        setConfirmStartOpen(false);
        setSelectedCampaignId(null);
      },
    }
  );

  const pauseCampaignMutation = useMutation(
    (id: string) => campaignAPI.pauseCampaign(id),
    {
      onError: (err: any) => {
        console.error(err);
        showToast(
          err?.message ? err.message : 'Failed to pause campaign',
          'error'
        );
      },
      onSuccess: () => {
        showToast('Campaign paused successfully', 'success');
        getCampaignsQuery.refetch();
      },
    }
  );

  const resumeCampaignMutation = useMutation(
    (id: string) => campaignAPI.resumeCampaign(id),
    {
      onError: (err: any) => {
        console.error(err);
        showToast(
          err?.message ? err.message : 'Failed to resume campaign',
          'error'
        );
      },
      onSuccess: () => {
        showToast('Campaign resumed successfully', 'success');
        getCampaignsQuery.refetch();
      },
    }
  );

  // Create Campaign Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [businessDays, setBusinessDays] = useState<Record<string, boolean>>({
    mon: true,
    tue: true,
    wed: true,
    thu: true,
    fri: true,
    sat: false,
    sun: false,
  });
  const [startTime, setStartTime] = useState('09:00 AM');
  const [endTime, setEndTime] = useState('06:00 PM');
  const [timezone, setTimezone] = useState('America/New_York');
  const [maxCallDurationSec, setMaxCallDurationSec] = useState<number>(120);
  const [ttsVoiceModel, setTtsVoiceModel] = useState('aura-asteria-en');

  const campaigns = useMemo(() => {
    return getCampaignsQuery.data?.[0] ?? [];
  }, [getCampaignsQuery.data]);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCampaign, setViewCampaign] = useState<Campaign | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);
  const [originalCampaign, setOriginalCampaign] = useState<Campaign | null>(
    null
  );

  // Confirm modal states
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmStartOpen, setConfirmStartOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null
  );

  const canCreateCampaign = useMemo(
    () =>
      !!selectedFile &&
      !!campaignName &&
      !!startTime &&
      !!endTime &&
      !!timezone &&
      !!selectedTemplateId &&
      !!maxCallDurationSec &&
      maxCallDurationSec > 0 &&
      !!ttsVoiceModel &&
      Object.values(businessDays).includes(true),
    [
      businessDays,
      campaignName,
      endTime,
      maxCallDurationSec,
      selectedFile,
      selectedTemplateId,
      startTime,
      timezone,
      ttsVoiceModel,
    ]
  );

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setCampaignName('');
    setCampaignDescription('');
    setSelectedFile(null);
    setSelectedTemplateId('');
    setBusinessDays({
      mon: true,
      tue: true,
      wed: true,
      thu: true,
      fri: true,
      sat: false,
      sun: false,
    });
    setStartTime('09:00 AM');
    setEndTime('06:00 PM');
    setTimezone('America/New_York');
    setMaxCallDurationSec(120);
    setTtsVoiceModel('aura-asteria-en');
  };

  const handleCreateCampaign = () => {
    if (canCreateCampaign && selectedFile) {
      createCampaignMutation.mutate({
        businessDays,
        description: campaignDescription,
        endTime,
        file: selectedFile,
        maxCallDurationSec,
        name: campaignName,
        startTime,
        templateId: selectedTemplateId,
        timezone,
        ttsVoiceModel,
      });
    }
  };

  const handleOpenView = (campaign: Campaign) => {
    setViewCampaign(campaign);
    setViewModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
    setViewCampaign(null);
  };

  const handleOpenEdit = (campaign: Campaign) => {
    setOriginalCampaign(campaign);
    setEditCampaign({
      ...campaign,
      businessDays: parseBusinessDays(campaign.businessDays),
      startTime: campaign.startTime || '09:00 AM',
      endTime: campaign.endTime || '06:00 PM',
    });
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditCampaign(null);
    setOriginalCampaign(null);
  };

  const handleEditChange = (field: keyof Campaign, value: unknown) => {
    if (editCampaign) {
      setEditCampaign({
        ...editCampaign,
        [field]: value,
      });
    }
  };

  const handleUpdateClick = () => {
    setConfirmUpdateOpen(true);
  };

  const handleConfirmUpdate = () => {
    if (editCampaign && originalCampaign) {
      updateCampaignMutation.mutate({
        id: editCampaign.id,
        data: {
          name: editCampaign.name,
          description: editCampaign.description,
          maxCallDurationSec: editCampaign.maxCallDurationSec,
          ttsVoiceModel: editCampaign.ttsVoiceModel,
          businessDays: editCampaign.businessDays,
          startTime: editCampaign.startTime,
          endTime: editCampaign.endTime,
        },
      });
    }
  };

  const handleDeleteClick = (id: string) => {
    setSelectedCampaignId(id);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedCampaignId) {
      deleteCampaignMutation.mutate(selectedCampaignId);
    }
  };

  const handleStartClick = (id: string) => {
    setSelectedCampaignId(id);
    setConfirmStartOpen(true);
  };

  const handleConfirmStart = () => {
    if (selectedCampaignId) {
      startCampaignMutation.mutate(selectedCampaignId);
    }
  };

  const handlePauseClick = (id: string) => {
    pauseCampaignMutation.mutate(id);
  };

  const handleResumeClick = (id: string) => {
    resumeCampaignMutation.mutate(id);
  };

  const handleApplyFilters = () => {
    const params: Record<string, string> = {};
    if (inputFilters.name) params.name = inputFilters.name;
    if (inputFilters.createdAtFrom) params.createdAtFrom = inputFilters.createdAtFrom;
    if (inputFilters.createdAtTo) params.createdAtTo = inputFilters.createdAtTo;
    if (inputFilters.status) params.status = inputFilters.status;
    setSearchParams(params);
    setAppliedFilters(inputFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    const empty = { name: '', createdAtFrom: '', createdAtTo: '', status: '' };
    setInputFilters(empty);
    setAppliedFilters(empty);
    setSearchParams({});
    setPage(0);
  };

  const isFormValid = useMemo(() => {
    if (!editCampaign) return false;
    return (
      !!editCampaign.name &&
      !!editCampaign.maxCallDurationSec &&
      editCampaign.maxCallDurationSec > 0 &&
      !!editCampaign.ttsVoiceModel &&
      !!editCampaign.startTime &&
      !!editCampaign.endTime
    );
  }, [editCampaign]);

  const changedFields = getChangedFields(originalCampaign, editCampaign);

  const renderFieldValue = (
    campaign: Campaign,
    key: string
  ): React.ReactNode => {
    switch (key) {
      case 'status':
        return (
          <Chip
            label={capitalize(campaign.status)}
            size='small'
            color={
              campaign.status === 'running'
                ? 'success'
                : campaign.status === 'failed'
                ? 'error'
                : campaign.status === 'completed'
                ? 'info'
                : 'default'
            }
          />
        );
      case 'createdAt':
        return formatDate(campaign.createdAt);
      case 'startedAt':
        return formatDate(campaign.startedAt);
      case 'completedAt':
        return formatDate(campaign.completedAt);
      case 'errorMessage':
        return campaign.errorMessage || '-';
      case 'llmTemplate':
        return campaign.llmTemplate?.name || '-';
      case 'llmTemplateIsDefault':
        return campaign.llmTemplate?.isDefault ? 'Yes' : 'No';
      case 'user':
        return campaign.user?.name || '-';
      case 'businessDays': {
        let days = campaign.businessDays;
        if (!days) return '-';
        // Parse if businessDays comes as a string
        if (typeof days === 'string') {
          try {
            days = JSON.parse(days) as Record<string, boolean>;
          } catch {
            return String(days); // Return as-is if parsing fails
          }
        }
        const activeDays = Object.entries(days as Record<string, boolean>)
          .filter(([, isActive]) => !!isActive)
          .map(([day]) => capitalize(day))
          .join(', ');
        return activeDays || '-';
      }
      case 'businessHours': {
        const start = campaign.startTime;
        const end = campaign.endTime;
        if (!start || !end) return '-';
        return `${start} - ${end}`;
      }
      case 'timezone':
        return campaign.timezone || '-';
      case 'ttsProvider':
        return campaign.ttsProvider || '-';
      case 'maxCallDurationSec':
        return campaign.maxCallDurationSec !== undefined
          ? `${campaign.maxCallDurationSec} sec`
          : '-';
      case 'ttsVoiceModel':
        return (
          VOICE_LABELS[campaign.ttsVoiceModel || ''] ||
          campaign.ttsVoiceModel ||
          '-'
        );
      case 'pauseReason': {
        const pauseReasonMap: Record<string, string> = {
          after_hours: 'Out of business hours',
          holiday: 'Holiday',
          manual: 'Manual',
          weekend: 'Weekend',
          tts_error: 'TTS Error',
        };
        const reason = campaign.pauseReason;
        if (!reason) return '-';
        return pauseReasonMap[reason] || reason;
      }
      default:
        const value = (campaign as unknown as Record<string, unknown>)[key];
        if (value === undefined || value === null || value === '') {
          return <span style={{ color: '#888' }}>(empty)</span>;
        }
        return String(value);
    }
  };

  const renderCampaignsContent = () => {
    if (getCampaignsQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const total = getCampaignsQuery.data?.[1] ?? 0;

    if (!campaigns || campaigns.length === 0) {
      return (
        <Typography color='text.secondary'>No campaigns available.</Typography>
      );
    }

    return (
      <>
      <TableContainer
        component={Paper}
        variant='outlined'
      >
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell>
                <Typography variant='subtitle2'>Name</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2'>Created At</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2'>Status</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2'>Template</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2'>Voice</Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography variant='subtitle2'>Actions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => {
              const canStart = canStartCampaign(campaign.status);
              const canPause = canPauseCampaign(campaign.status);
              const canEdit = canEditCampaign(campaign.status);
              const canDelete = canDeleteCampaign(campaign.status);
              const startTooltip = canStart
                ? 'Start'
                : getStartDisabledTooltip(campaign.status);
              const editTooltip = canEdit
                ? 'Edit'
                : getEditDisabledTooltip(campaign.status);
              const deleteTooltip = canDelete
                ? 'Delete'
                : getDeleteDisabledTooltip(campaign.status);

              // Determine which action icon to show
              const isPaused = campaign.status.toLowerCase() === 'paused';

              return (
                <TableRow
                  key={campaign.id}
                  hover
                >
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{formatDate(campaign.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={capitalize(campaign.status)}
                      size='small'
                      color={
                        campaign.status === 'running'
                          ? 'success'
                          : campaign.status === 'failed'
                          ? 'error'
                          : campaign.status === 'completed'
                          ? 'info'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>{campaign.llmTemplate?.name || '-'}</TableCell>
                  <TableCell>
                    {VOICE_LABELS[campaign.ttsVoiceModel || ''] ||
                      campaign.ttsVoiceModel ||
                      '-'}
                  </TableCell>
                  <TableCell align='right'>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                      }}
                    >
                      <Tooltip title='View'>
                        <IconButton
                          size='small'
                          onClick={() => handleOpenView(campaign)}
                          color='primary'
                        >
                          <Visibility fontSize='small' />
                        </IconButton>
                      </Tooltip>

                      {/* Start/Pause/Resume/Scheduled Icon */}
                      {canPause ? (
                        <Tooltip title='Pause campaign'>
                          <IconButton
                            size='small'
                            onClick={() => handlePauseClick(campaign.id)}
                            disabled={pauseCampaignMutation.isLoading}
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              border: '2px solid',
                              borderColor: 'warning.main',
                              color: 'warning.main',
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: 'warning.light',
                              },
                            }}
                          >
                            <Pause sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : isPaused ? (
                        (() => {
                          const canManualResume =
                            campaign.pauseReason === 'manual';
                          const resumeTooltip = canManualResume
                            ? 'Resume campaign'
                            : `Cannot resume: campaign was paused automatically (${
                                campaign.pauseReason || 'unknown reason'
                              })`;
                          return (
                            <Tooltip title={resumeTooltip}>
                              <span>
                                <IconButton
                                  size='small'
                                  onClick={() => handleResumeClick(campaign.id)}
                                  disabled={
                                    !canManualResume ||
                                    resumeCampaignMutation.isLoading
                                  }
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    border: '2px solid',
                                    borderColor: canManualResume
                                      ? 'info.main'
                                      : 'grey.400',
                                    color: canManualResume
                                      ? 'info.main'
                                      : 'grey.400',
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                      backgroundColor: canManualResume
                                        ? 'info.light'
                                        : 'transparent',
                                    },
                                  }}
                                >
                                  <PlayArrow sx={{ fontSize: 14 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        })()
                      ) : (
                        <Tooltip title={startTooltip}>
                          <span>
                            <IconButton
                              size='small'
                              onClick={() => handleStartClick(campaign.id)}
                              disabled={
                                !canStart || startCampaignMutation.isLoading
                              }
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: canStart
                                  ? 'success.main'
                                  : 'grey.300',
                                color: canStart ? 'success.main' : 'grey.400',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                  backgroundColor: canStart
                                    ? 'success.light'
                                    : 'transparent',
                                },
                              }}
                            >
                              <PlayArrow sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      <Tooltip title={editTooltip}>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() => handleOpenEdit(campaign)}
                            color='primary'
                            disabled={!canEdit}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={deleteTooltip}>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() => handleDeleteClick(campaign.id)}
                            color='error'
                            disabled={
                              !canDelete || deleteCampaignMutation.isLoading
                            }
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component='div'
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
        showFirstButton
        showLastButton
      />
    </>
    );
  };

  return (
    <MainLayout>
      <Stack
        spacing={4}
        width='100%'
      >
        <Typography
          variant='h4'
          gutterBottom
        >
          Campaigns
        </Typography>

        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              '& .MuiAccordionSummary-content': {
                justifyContent: 'space-between',
                alignItems: 'center',
                pr: 2,
              },
            }}
          >
            <Typography variant='h6'>Campaigns</Typography>
            <Button
              variant='contained'
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleOpenCreateModal();
              }}
            >
              Create
            </Button>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                mb: 2,
                alignItems: 'center',
              }}
            >
              <TextField
                label='Campaign Name'
                size='small'
                value={inputFilters.name}
                onChange={(e) =>
                  setInputFilters((prev) => ({ ...prev, name: e.target.value }))
                }
                sx={{ minWidth: 200 }}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label='From'
                  value={
                    inputFilters.createdAtFrom
                      ? dayjs(inputFilters.createdAtFrom)
                      : null
                  }
                  onChange={(date) =>
                    setInputFilters((prev) => ({
                      ...prev,
                      createdAtFrom: date ? date.format('YYYY-MM-DD') : '',
                    }))
                  }
                  slotProps={{
                    textField: { size: 'small', sx: { minWidth: 150 } },
                  }}
                />
                <DatePicker
                  label='To'
                  value={
                    inputFilters.createdAtTo
                      ? dayjs(inputFilters.createdAtTo)
                      : null
                  }
                  onChange={(date) =>
                    setInputFilters((prev) => ({
                      ...prev,
                      createdAtTo: date ? date.format('YYYY-MM-DD') : '',
                    }))
                  }
                  slotProps={{
                    textField: { size: 'small', sx: { minWidth: 150 } },
                  }}
                />
              </LocalizationProvider>
              <FormControl
                size='small'
                sx={{ minWidth: 150 }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={inputFilters.status}
                  label='Status'
                  onChange={(e) =>
                    setInputFilters((prev) => ({
                      ...prev,
                      status: e.target.value as string,
                    }))
                  }
                >
                  <MenuItem value=''>
                    <em>All</em>
                  </MenuItem>
                  {CAMPAIGN_STATUSES.map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                    >
                      {STATUS_LABELS[status] || status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant='contained'
                size='small'
                onClick={handleApplyFilters}
              >
                Apply
              </Button>
              <Button
                variant='outlined'
                size='small'
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </Box>
            {renderCampaignsContent()}
          </AccordionDetails>
        </Accordion>

        {/* Create Campaign Modal */}
        <CreateCampaignModal
          open={createModalOpen}
          campaignName={campaignName}
          campaignDescription={campaignDescription}
          selectedFile={selectedFile}
          selectedTemplateId={selectedTemplateId}
          templates={getTemplatesQuery.data || []}
          templatesLoading={getTemplatesQuery.isLoading}
          businessDays={businessDays}
          startTime={startTime}
          endTime={endTime}
          timezone={timezone}
          isLoading={createCampaignMutation.isLoading}
          canCreate={canCreateCampaign}
          onClose={handleCloseCreateModal}
          onCreate={handleCreateCampaign}
          onCampaignNameChange={setCampaignName}
          onCampaignDescriptionChange={setCampaignDescription}
          onFileSelect={setSelectedFile}
          onFileRemove={() => setSelectedFile(null)}
          onTemplateIdChange={setSelectedTemplateId}
          onBusinessDaysChange={setBusinessDays}
          onStartTimeChange={setStartTime}
          onEndTimeChange={setEndTime}
          onTimezoneChange={setTimezone}
          maxCallDurationSec={maxCallDurationSec}
          onMaxCallDurationSecChange={setMaxCallDurationSec}
          ttsVoiceModel={ttsVoiceModel}
          onTtsVoiceModelChange={setTtsVoiceModel}
        />

        {/* View Modal */}
        <Dialog
          open={viewModalOpen}
          onClose={handleCloseView}
          maxWidth='md'
          fullWidth
        >
          <DialogTitle>View Campaign</DialogTitle>
          <DialogContent>
            {viewCampaign && (
              <Paper
                variant='outlined'
                sx={{
                  p: 2,
                  backgroundColor: 'grey.50',
                  fontFamily: 'monospace',
                }}
              >
                {(() => {
                  // Insert pauseReason right after status if campaign is paused
                  const fields = [...FIELD_ORDER];
                  if (viewCampaign.status.toLowerCase() === 'paused') {
                    const statusIndex = fields.indexOf('status');
                    if (statusIndex !== -1) {
                      fields.splice(statusIndex + 1, 0, 'pauseReason');
                    }
                  }
                  return fields;
                })().map((key) => (
                  <Box
                    key={key}
                    sx={{ mb: 2 }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: 'primary.main',
                      }}
                    >
                      {FIELD_LABELS[key]}:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        pl: 1,
                      }}
                    >
                      {renderFieldValue(viewCampaign, key)}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseView}
              variant='contained'
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Modal */}
        <Dialog
          open={editModalOpen}
          onClose={handleCloseEdit}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Edit Campaign</DialogTitle>
          <DialogContent>
            {editCampaign && (
              <Stack
                spacing={2}
                sx={{ mt: 1 }}
              >
                <TextField
                  fullWidth
                  required
                  label='Name'
                  value={editCampaign.name}
                  onChange={(e) => handleEditChange('name', e.target.value)}
                  variant='outlined'
                  size='small'
                />

                <TextField
                  fullWidth
                  label='Description'
                  value={editCampaign.description || ''}
                  onChange={(e) =>
                    handleEditChange('description', e.target.value)
                  }
                  variant='outlined'
                  size='small'
                  multiline
                  rows={3}
                />

                <TextField
                  fullWidth
                  label='Timezone'
                  value={editCampaign.timezone || 'America/New_York'}
                  disabled
                  variant='outlined'
                  size='small'
                />

                <TextField
                  fullWidth
                  required
                  label='Max Call Duration (sec)'
                  type='number'
                  value={editCampaign.maxCallDurationSec || ''}
                  onChange={(e) =>
                    handleEditChange(
                      'maxCallDurationSec',
                      Number(e.target.value)
                    )
                  }
                  variant='outlined'
                  size='small'
                />

                <FormControl
                  fullWidth
                  size='small'
                  required
                >
                  <InputLabel>Voice Model</InputLabel>
                  <Select
                    value={editCampaign.ttsVoiceModel || 'aura-asteria-en'}
                    label='Voice Model'
                    onChange={(e) =>
                      handleEditChange('ttsVoiceModel', e.target.value)
                    }
                  >
                    {VOICE_OPTIONS.map((voice) => (
                      <MenuItem
                        key={voice.value}
                        value={voice.value}
                      >
                        {voice.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Business Days */}
                <Box>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                  >
                    Business Days
                  </Typography>
                  <FormGroup row>
                    {WEEK_DAYS.map((day) => (
                      <FormControlLabel
                        key={day.key}
                        control={
                          <Checkbox
                            checked={
                              editCampaign.businessDays?.[day.key] ?? false
                            }
                            onChange={(e) =>
                              handleEditChange('businessDays', {
                                ...editCampaign.businessDays,
                                [day.key]: e.target.checked,
                              })
                            }
                            size='small'
                          />
                        }
                        label={
                          <Typography variant='body2'>{day.label}</Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>

                {/* Business Hours */}
                <Box>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                  >
                    Business Hours
                  </Typography>
                  <Stack
                    direction='row'
                    spacing={2}
                    alignItems='center'
                  >
                    <FormControl
                      fullWidth
                      size='small'
                      required
                    >
                      <InputLabel>Start Time</InputLabel>
                      <Select
                        value={editCampaign.startTime || ''}
                        label='Start Time'
                        onChange={(e) =>
                          handleEditChange('startTime', e.target.value)
                        }
                      >
                        {HOURS.map((hour) => (
                          <MenuItem
                            key={hour}
                            value={hour}
                          >
                            {hour}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant='body2'>-</Typography>
                    <FormControl
                      fullWidth
                      size='small'
                      required
                    >
                      <InputLabel>End Time</InputLabel>
                      <Select
                        value={editCampaign.endTime || ''}
                        label='End Time'
                        onChange={(e) =>
                          handleEditChange('endTime', e.target.value)
                        }
                      >
                        {HOURS.map((hour) => (
                          <MenuItem
                            key={hour}
                            value={hour}
                          >
                            {hour}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseEdit}
              disabled={updateCampaignMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateClick}
              variant='contained'
              disabled={
                updateCampaignMutation.isLoading ||
                !isFormValid ||
                changedFields.length === 0
              }
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Start Modal */}
        <ConfirmModal
          open={confirmStartOpen}
          title='Start Campaign'
          onCancel={() => {
            setConfirmStartOpen(false);
            setSelectedCampaignId(null);
          }}
          onConfirm={handleConfirmStart}
          isLoading={startCampaignMutation.isLoading}
          confirmText='Start'
          confirmColor='success'
        >
          Are you sure you want to start this campaign?
        </ConfirmModal>

        {/* Confirm Update Modal */}
        <ConfirmModal
          open={confirmUpdateOpen}
          title='Confirm Update'
          onCancel={() => setConfirmUpdateOpen(false)}
          onConfirm={handleConfirmUpdate}
          isLoading={updateCampaignMutation.isLoading}
          confirmText='Update'
        >
          <Stack spacing={1}>
            <Typography
              variant='body1'
              gutterBottom
            >
              Are you sure you want to update this campaign? The following
              changes will be applied:
            </Typography>
            {changedFields.map((change) => (
              <Paper
                key={change.field}
                variant='outlined'
                sx={{ p: 1.5, backgroundColor: 'grey.50' }}
              >
                <Typography
                  variant='subtitle2'
                  color='primary'
                  gutterBottom
                >
                  {FIELD_LABELS[change.field] || change.field}
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    <strong>Old:</strong> {change.oldValue}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>New:</strong> {change.newValue}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        </ConfirmModal>

        {/* Confirm Delete Modal */}
        <ConfirmModal
          open={confirmDeleteOpen}
          title='Delete Campaign'
          onCancel={() => {
            setConfirmDeleteOpen(false);
            setSelectedCampaignId(null);
          }}
          onConfirm={handleConfirmDelete}
          isLoading={deleteCampaignMutation.isLoading}
          confirmText='Delete'
          confirmColor='error'
        >
          Are you sure you want to delete this campaign? This action cannot be
          undone.
        </ConfirmModal>
      </Stack>
    </MainLayout>
  );
};
